import React, { useState, useEffect } from 'react'
import { Clock, Users, Calendar, Check, ChevronRight, X, Hash, UserPlus, Mail } from 'lucide-react'
import { supabase, getCurrentSession } from '../lib/supabase'
import { getSlackInstallUrl } from '../lib/supabase'

interface WelcomeModalProps {
  isOpen: boolean
  onClose: () => void
  onComplete: () => void
  teamName: string
}

const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago', 
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Australia/Sydney'
]

const DAYS_OF_WEEK = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' }
]

interface StandupConfig {
  enabled: boolean
  dm_time: string
  timezone: string
  days_of_week: number[]
  ai_summary_enabled: boolean
  standup_channel: string | null
}

interface SlackChannel {
  id: string
  name: string
  is_channel: boolean
  is_private: boolean
  is_member: boolean
}

interface SlackUser {
  id: string
  name: string
  email: string | null
  avatar: string
  isAlreadyMember: boolean
  title: string | null
  displayName: string
}

export const WelcomeModal: React.FC<WelcomeModalProps> = ({ 
  isOpen, 
  onClose, 
  onComplete,
  teamName 
}) => {
  const [currentStep, setCurrentStep] = useState(1)
  const [channels, setChannels] = useState<SlackChannel[]>([])
  const [loadingChannels, setLoadingChannels] = useState(false)
  const [needsSlackInstall, setNeedsSlackInstall] = useState(false)
  const [slackTeamId, setSlackTeamId] = useState<string | null>(null)
  const [slackUsers, setSlackUsers] = useState<SlackUser[]>([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set())
  const [invitingUsers, setInvitingUsers] = useState(false)
  const [config, setConfig] = useState<StandupConfig>({
    enabled: true,
    dm_time: '09:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    days_of_week: [1, 2, 3, 4, 5], // Mon-Fri
    ai_summary_enabled: true,
    standup_channel: null
  })

  // Load Slack channels when modal opens
  useEffect(() => {
    if (isOpen && currentStep === 4) {
      loadSlackChannels()
    }
  }, [isOpen, currentStep])

  // Load Slack users when on step 5
  useEffect(() => {
    if (isOpen && currentStep === 5) {
      loadSlackUsers()
    }
  }, [isOpen, currentStep])

  const loadSlackUsers = async () => {
    setLoadingUsers(true)
    try {
      const teamId = window.location.pathname.split('/').pop()
      if (!teamId) return

      const session = await getCurrentSession()
      if (!session) {
        console.error('No session found')
        return
      }

      const { data, error } = await supabase.functions.invoke('get-slack-users', {
        body: { team_id: teamId },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (error) {
        console.error('Error loading users:', error)
      } else {
        setSlackUsers(data?.users || [])
        // Pre-select users who aren't already members
        const nonMembers = (data?.users || []).filter((user: SlackUser) => !user.isAlreadyMember)
        setSelectedUsers(new Set(nonMembers.map((user: SlackUser) => user.id)))
      }
    } catch (error) {
      console.error('Error loading users:', error)
    } finally {
      setLoadingUsers(false)
    }
  }

  const loadSlackChannels = async () => {
    setLoadingChannels(true)
    setNeedsSlackInstall(false)
    try {
      // Get current team from URL
      const teamId = window.location.pathname.split('/').pop()
      if (!teamId) return

      // First, check if the team has a bot token
      const { data: team, error: teamError } = await supabase
        .from('teams')
        .select('slack_team_id, slack_bot_token')
        .eq('id', teamId)
        .single()

      if (teamError || !team) {
        console.error('Error loading team:', teamError)
        return
      }

      setSlackTeamId(team.slack_team_id)

      // If no bot token, show install prompt
      if (!team.slack_bot_token) {
        setNeedsSlackInstall(true)
        setLoadingChannels(false)
        return
      }

      // Get the current user's session token
      const session = await getCurrentSession()
      
      // Call edge function to get Slack channels
      const { data, error } = await supabase.functions.invoke('get-slack-channels', {
        body: { team_id: teamId },
        headers: session ? {
          Authorization: `Bearer ${session.access_token}`
        } : undefined
      })

      if (error) {
        console.error('Error loading channels:', error)
        // Check if the error is due to missing bot token or missing scopes
        if (error.message?.includes('bot token not available') || 
            error.message?.includes('missing_scope') ||
            error.message?.includes('Failed to fetch channels')) {
          setNeedsSlackInstall(true)
        }
      } else {
        setChannels(data?.channels || [])
      }
    } catch (error) {
      console.error('Error loading channels:', error)
    } finally {
      setLoadingChannels(false)
    }
  }

  if (!isOpen) return null

  const handleNext = async () => {
    if (currentStep === 5) {
      // If we're on the last step, invite users (if any selected) and complete
      if (selectedUsers.size > 0) {
        await inviteSelectedUsers()
      }
      handleComplete()
    } else if (currentStep < 5) {
      setCurrentStep(currentStep + 1)
    }
  }

  const inviteSelectedUsers = async () => {
    if (selectedUsers.size === 0) return
    
    setInvitingUsers(true)
    try {
      const teamId = window.location.pathname.split('/').pop()
      if (!teamId) return

      const session = await getCurrentSession()
      if (!session) {
        console.error('No session found')
        return
      }

      // Get the selected users' details
      const usersToInvite = slackUsers
        .filter(user => selectedUsers.has(user.id) && !user.isAlreadyMember)
        .map(user => ({
          slack_user_id: user.id,
          name: user.name,
          email: user.email
        }))
      
      if (usersToInvite.length === 0) return

      // Call edge function to invite users
      const { data, error } = await supabase.functions.invoke('invite-team-members', {
        body: { 
          team_id: teamId,
          users: usersToInvite
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      })

      if (error) {
        console.error('Error inviting users:', error)
      } else {
        console.log(`Successfully invited ${data?.invited || 0} users to the team`)
        if (data?.errors?.length > 0) {
          console.error('Some users could not be invited:', data.errors)
        }
      }
    } catch (error) {
      console.error('Error inviting users:', error)
    } finally {
      setInvitingUsers(false)
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleComplete = async () => {
    // Save the configuration and complete onboarding
    try {
      
      // Get team ID from URL or context
      const teamId = window.location.pathname.split('/').pop()
      
      if (teamId) {
        await supabase
          .from('teams')
          .update({ standup_config: config as any })
          .eq('id', teamId)
      }
      
      onComplete()
    } catch (error) {
      console.error('Failed to save configuration:', error)
      // Still complete the onboarding, user can configure later
      onComplete()
    }
  }

  const handleDayToggle = (day: number) => {
    setConfig(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day].sort()
    }))
  }

  const stepTitles = [
    'Welcome to SyncSimple!',
    'Set Your Standup Schedule',
    'Configure AI Summaries',
    'Choose Standup Channel',
    'Invite Team Members'
  ]

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-8 py-6 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{stepTitles[currentStep - 1]}</h2>
            <p className="text-sm text-gray-500 mt-1">Step {currentStep} of 5</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-8 py-4">
          <div className="flex items-center space-x-2">
            {[1, 2, 3, 4, 5].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step <= currentStep 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {step}
                </div>
                {step < 5 && (
                  <div className={`w-8 h-0.5 ${
                    step < currentStep ? 'bg-indigo-600' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="px-8 py-6">
          {currentStep === 1 && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto">
                <Users className="h-10 w-10 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Welcome {teamName}!
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Let's get your team set up with daily standups in just a few quick steps. 
                  We'll configure when your team receives standup reminders and how AI summaries work.
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">What you'll set up:</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Daily standup reminder schedule</li>
                  <li>• Team timezone and working days</li>
                  <li>• AI-powered team summaries</li>
                  <li>• Slack channel for posting summaries</li>
                  <li>• Invite your team members</li>
                </ul>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 text-indigo-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  When should we remind your team?
                </h3>
                <p className="text-gray-600">
                  Choose the best time and days for your team's standup reminders.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reminder Time
                  </label>
                  <input
                    type="time"
                    value={config.dm_time}
                    onChange={(e) => setConfig(prev => ({ ...prev, dm_time: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Timezone
                  </label>
                  <select
                    value={config.timezone}
                    onChange={(e) => setConfig(prev => ({ ...prev, timezone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {TIMEZONES.map(tz => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Which days should we send reminders?
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {DAYS_OF_WEEK.map(day => (
                    <button
                      key={day.value}
                      onClick={() => handleDayToggle(day.value)}
                      className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        config.days_of_week.includes(day.value)
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {day.label.slice(0, 3)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="h-8 w-8 text-indigo-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  AI Team Summaries
                </h3>
                <p className="text-gray-600">
                  Let AI automatically create daily summaries of your team's progress.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className={`w-6 h-6 rounded border-2 flex items-center justify-center cursor-pointer transition-colors ${
                      config.ai_summary_enabled 
                        ? 'bg-indigo-600 border-indigo-600 text-white' 
                        : 'border-gray-300 bg-white'
                    }`}
                    onClick={() => setConfig(prev => ({ ...prev, ai_summary_enabled: !prev.ai_summary_enabled }))}
                    >
                      {config.ai_summary_enabled && <Check className="h-4 w-4" />}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">
                      Enable AI Team Summaries
                    </h4>
                    <p className="text-sm text-gray-600 mb-4">
                      When your team has multiple standups on the same day, AI will create a summary highlighting:
                    </p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Key accomplishments across the team</li>
                      <li>• Upcoming tasks and priorities</li>
                      <li>• Blockers that need attention</li>
                      <li>• Overall team sentiment and mood</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Hash className="h-8 w-8 text-indigo-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Choose Standup Channel
                </h3>
                <p className="text-gray-600">
                  Select a Slack channel where AI summaries will be posted (optional).
                </p>
              </div>

              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Individual standup reminders are always sent via direct message. 
                    Channel posting is only for team summaries when AI summaries are enabled.
                  </p>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-purple-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-purple-900 mb-1">Using a Private Channel?</h4>
                      <p className="text-sm text-purple-800">
                        Private channels only appear here after you invite SyncSimple to them. To use a private channel:
                      </p>
                      <ol className="text-sm text-purple-800 mt-2 ml-4 space-y-1 list-decimal">
                        <li>Go to your private channel in Slack</li>
                        <li>Type <code className="bg-purple-100 px-1.5 py-0.5 rounded">/invite @SyncSimple</code></li>
                        <li>Come back here and click "Refresh Channels" below</li>
                      </ol>
                      <p className="text-xs text-purple-700 mt-2 italic">
                        Note: Public channels appear automatically and SyncSimple will join them when you select one.
                      </p>
                    </div>
                  </div>
                </div>

                {needsSlackInstall ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">Slack App Installation Required</h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>To complete the setup, you need to install the SyncSimple app to your Slack workspace.</p>
                          <p className="mt-2">This will enable:</p>
                          <ul className="list-disc list-inside mt-1">
                            <li>The <code className="bg-yellow-100 px-1 rounded">/standup</code> command</li>
                            <li>Daily standup reminders</li>
                            <li>AI-powered team summaries</li>
                          </ul>
                        </div>
                        <div className="mt-4">
                          <button
                            onClick={async () => {
                              const installUrl = await getSlackInstallUrl(slackTeamId || undefined)
                              window.open(installUrl, '_blank', 'noopener,noreferrer')
                              // After opening the install window, close the modal
                              // The user will need to refresh after installing
                              setTimeout(() => {
                                onClose()
                              }, 2000)
                            }}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#4A154B] hover:bg-[#611f69] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4A154B]"
                          >
                            <img src="/Slack_icon_2019.svg" alt="Slack" className="h-4 w-4 mr-2" />
                            Install Slack App
                          </button>
                          <p className="mt-2 text-xs text-yellow-700">
                            After installing, please refresh this page to continue setup.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : loadingChannels ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading channels...</p>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">Select a channel:</span>
                      <button
                        onClick={loadSlackChannels}
                        className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refresh Channels
                      </button>
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      <div
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          config.standup_channel === null
                            ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setConfig(prev => ({ ...prev, standup_channel: null }))}
                      >
                        <div className="flex items-center">
                          <div className="w-6 h-6 rounded border-2 border-gray-400 flex items-center justify-center mr-3 bg-gray-100">
                            <X className="h-4 w-4 text-gray-600" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">No channel (Direct messages only)</div>
                            <div className="text-sm text-gray-500">Team summaries won't be posted to any channel</div>
                          </div>
                        </div>
                      </div>

                    {channels.map((channel) => (
                      <div
                        key={channel.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          config.standup_channel === channel.id
                            ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => {
                          setConfig(prev => ({ ...prev, standup_channel: channel.id }))
                        }}
                      >
                        <div className="flex items-center">
                          <div className="w-6 h-6 rounded border-2 border-gray-400 flex items-center justify-center mr-3">
                            <Hash className="h-4 w-4 text-gray-600" />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">
                              #{channel.name}
                              {channel.is_private && <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">🔒 Private</span>}
                            </div>
                            <div className="text-sm text-gray-500">
                              {channel.is_private ? 'Private channel - bot must be invited' : 'Public channel - bot will post without joining'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {channels.length === 0 && !loadingChannels && (
                      <div className="text-center py-8 text-gray-500">
                        <Hash className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p>No channels found. Make sure the Slack app has access to channels.</p>
                      </div>
                    )}
                    </div>
                  </>
                )}
              </div>

            </div>
          )}

          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <UserPlus className="h-8 w-8 text-indigo-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Invite Your Team
                </h3>
                <p className="text-gray-600">
                  Select team members from your Slack workspace to start using SyncSimple.
                </p>
              </div>

              {loadingUsers ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading team members...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Select/Deselect All */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="select-all"
                        checked={selectedUsers.size === slackUsers.filter(u => !u.isAlreadyMember).length && selectedUsers.size > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const nonMembers = slackUsers.filter(u => !u.isAlreadyMember)
                            setSelectedUsers(new Set(nonMembers.map(u => u.id)))
                          } else {
                            setSelectedUsers(new Set())
                          }
                        }}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="select-all" className="ml-2 text-sm font-medium text-gray-700">
                        Select all ({slackUsers.filter(u => !u.isAlreadyMember).length} available)
                      </label>
                    </div>
                    <span className="text-sm text-gray-500">
                      {selectedUsers.size} selected
                    </span>
                  </div>

                  {/* User List */}
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {slackUsers.map((user) => (
                      <div
                        key={user.id}
                        className={`p-3 border rounded-lg ${
                          user.isAlreadyMember 
                            ? 'bg-gray-50 border-gray-200' 
                            : 'bg-white border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id={`user-${user.id}`}
                            checked={selectedUsers.has(user.id)}
                            disabled={user.isAlreadyMember}
                            onChange={(e) => {
                              const newSelected = new Set(selectedUsers)
                              if (e.target.checked) {
                                newSelected.add(user.id)
                              } else {
                                newSelected.delete(user.id)
                              }
                              setSelectedUsers(newSelected)
                            }}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded disabled:opacity-50"
                          />
                          <label htmlFor={`user-${user.id}`} className="ml-3 flex-1 flex items-center cursor-pointer">
                            {user.avatar && (
                              <img src={user.avatar} alt={user.name} className="h-8 w-8 rounded-full mr-3" />
                            )}
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">
                                {user.name}
                                {user.isAlreadyMember && (
                                  <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Already member</span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500">
                                {user.email || user.displayName}
                                {user.title && <span className="ml-2">• {user.title}</span>}
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>
                    ))}

                    {slackUsers.length === 0 && !loadingUsers && (
                      <div className="text-center py-8 text-gray-500">
                        <Users className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p>No team members found in your Slack workspace.</p>
                      </div>
                    )}
                  </div>

                  {selectedUsers.size > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-800">
                        <strong>Note:</strong> Selected users will be added to SyncSimple and can start submitting standups. 
                        They'll receive notifications based on your configured schedule.
                      </p>
                    </div>
                  )}

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-medium text-green-900 mb-2">🎉 Almost done!</h4>
                    <p className="text-sm text-green-800">
                      After inviting your team members, click "Complete Setup" to finish. 
                      Your team will start receiving standup reminders based on your schedule.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-6 border-t border-gray-200 flex justify-between">
          <button
            onClick={currentStep === 1 ? onClose : handleBack}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
          >
            {currentStep === 1 ? 'Skip Setup' : 'Back'}
          </button>
          
          <button
            onClick={handleNext}
            disabled={(currentStep === 4 && needsSlackInstall) || invitingUsers}
            className={`px-6 py-2 font-medium rounded-md transition-colors flex items-center ${
              (currentStep === 4 && needsSlackInstall) || invitingUsers
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
          >
            {invitingUsers ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-2"></div>
                Inviting...
              </>
            ) : currentStep === 5 ? 'Complete Setup' : 'Next'}
            {currentStep < 5 && !invitingUsers && <ChevronRight className="ml-1 h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  )
}

export default WelcomeModal